/* -----------------Globals--------------- */
import { useLocalSearchParams, useRouter } from "expo-router";

/* -----------------UI--------------- */
import { Appbar } from "react-native-paper";

import { cacheKeys } from "@constants/cache-keys";
import { events } from "@constants/tracking";
import { pageViewTypes } from "@constants/tracking";
import { isWeb } from "@helpers/platform";
import useAnalytics from "@hooks/useAnalytics";
import { useDevice } from "@hooks/useDevice";
import { useProductDetails } from "@hooks/useProduct";
import { calculatePlanExpiry, getDurationText } from "@memorang/helpers";
/* -----------------Child components--------------- */
import { Box, Loader, ScrollContainer } from "@memorang/ui";
import { useQueryClient } from "@tanstack/react-query";
import { updateUserProperties } from "analytics";
import { AccessGrantedView } from "components/AccessGrantedView";
import DocumentTitle from "components/DocumentTitle";
import { CustomAppbar } from "components/index";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

const SuccessRoute = () => {
	const { data: productDetails } = useProductDetails();
	const router = useRouter();

	const { duration, interval, amount, currency, planId } =
		useLocalSearchParams<{
			duration: string;
			interval: string;
			amount: string;
			currency: string;
			planId: string;
		}>();

	const { explicitCallTrackCustomEvent } = useAnalytics({
		type: pageViewTypes.purchaseSuccess,
	});

	const { isMobile } = useDevice();
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	const headerTitle = t("purchase.success.headerTitle");

	useEffect(() => {
		if (productDetails) {
			updateUserProperties({
				premium_user: "true",
				products: JSON.stringify([
					{
						id: productDetails.id,
						type: "study_pack",
					},
				]),
			});
			explicitCallTrackCustomEvent({
				eventName: events.paymentSuccessful,
				product_id: productDetails.numericId,
				product_name: productDetails.name,
				plan_amount: amount,
				plan_currency: currency,
				plan_id: planId,
			});
			queryClient.invalidateQueries({
				queryKey: [cacheKeys.productMetadata],
			});
		}
	}, [
		productDetails,
		queryClient,
		explicitCallTrackCustomEvent,
		amount,
		currency,
		planId,
	]);

	if (!productDetails) {
		return (
			<Box flex={1} height="100%" justifyContent="center">
				<CustomAppbar
					options={{
						headerTitle,
						subtitle: isMobile ? "" : t("purchase.success.subtitle"),
						headerLeft: () => (
							<Appbar.BackAction
								onPress={() => router.replace("/(protected)/(tabs)/home")}
							/>
						),
						headerRight: () => null,
					}}
				/>
				<Loader />
			</Box>
		);
	}

	const planExpiry = duration
		? calculatePlanExpiry(interval, Number(duration))
		: undefined;
	const expirationDateText = duration
		? getDurationText(Number(duration), interval)
		: undefined;

	const {
		name,
		examName,
		id,
		icon: { url },
		features,
	} = productDetails;

	const title = t("purchase.success.accessGranted", { name });
	const subtitle = isMobile
		? ""
		: t("purchase.success.purchaseSuccessful", { name });

	const products = [
		{
			id,
			title: name,
			description: t("purchase.success.studyPack"),
			icon: url,
			features,
			isCustomAccess: false,
		},
	];
	const successfulPurchaseTitle = `Congratulations! You have unlocked **${expirationDateText}** access to the **${name}**. This will expire on **${planExpiry}**.`;

	return (
		<>
			<CustomAppbar
				options={{
					headerTitle,
					subtitle,
					headerRight: () => null,
					headerLeft: () => (
						<Appbar.BackAction
							onPress={() => {
								//Not sure but for some reason the router.replace is not working on mobile
								if (isWeb) {
									router.replace("/(protected)/(tabs)/home");
								} else {
									router.back();
								}
							}}
						/>
					),
				}}
			/>
			<DocumentTitle title={t("purchase.success.documentTitle")} />
			<ScrollContainer maxWidth="xs">
				<Box paddingTop={24} paddingBottom={44}>
					<AccessGrantedView
						products={products}
						examName={examName}
						title={title}
						successfulPurchaseTitle={successfulPurchaseTitle}
					/>
				</Box>
			</ScrollContainer>
		</>
	);
};

export default SuccessRoute;
